import { type FC, useState } from 'react';
import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { Button, Header, Text } from '../../../ui-components';
import { GetIcon } from '../../icons';
import { AnswerField } from './AnswerField';
import { AnswerReplyItem } from './AnswerReplyItem';
import type { AnswerTypes, UserTypes } from '../../../bff-api/turn-to/model';

export interface QuestionItemProps {
    text: string;
    dateCreated: string;
    bestAnswer: AnswerTypes;
    upVotes: number;

    user: UserTypes;
    id: number;
    answers: AnswerTypes[];
}

export const QuestionItem: FC<QuestionItemProps> = ({
    text,
    dateCreated,
    upVotes,
    bestAnswer,
    user,
    id,
    answers,
}) => {
    const [expandAnswers, setExpandAnswers] = useState<boolean>(false);
    const date = new Intl.DateTimeFormat('default', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(new Date(dateCreated));

    const seeAllAnswers = () => {
        setExpandAnswers(!expandAnswers);
    };

    const {
        isOpen: isAnswerTextfieldVisible,
        onOpen: onShowAnswerTextfield,
        onClose: onHideAnswerTextField,
    } = useDisclosure();

    return (
        <Box
            _first={{ paddingTop: 0 }}
            paddingY={4}
            sx={{
                borderBottom: '1px',
                borderColor: 'slt-pepper.400',
                borderType: 'solid',
            }}
            _last={{ borderBottom: 'none' }}
        >
            <Header margin="0 0 4px 0" as="h4" variant="header4">
                {text}
            </Header>
            <Flex gap={2} marginBottom={4}>
                {user?.firstName || user?.nickName ? (
                    <Text size="small">
                        {user?.firstName
                            ? `${user?.firstName} ${user?.lastName?.[0] ?? ''}`
                            : user?.nickName}
                    </Text>
                ) : null}
                <Text size="small" color="slt-pepper.700">
                    {date}
                </Text>
            </Flex>
            {bestAnswer && <AnswerReplyItem item={bestAnswer} type="bestAnswer" noPadding />}
            <Flex marginTop={6} justifyContent="space-between">
                <Flex className="space-x-4">
                    {answers.length > 0 ? (
                        <Button
                            variant="text-button"
                            onClick={seeAllAnswers}
                            rightIcon={<GetIcon name="dropdown-arrow-open" size="sm" />}
                        >
                            <Text style="bold" size="small">
                                <FormattedMessage
                                    defaultMessage="See {answersNumber} answers"
                                    id="turn-to.see-more-answers"
                                    values={{
                                        answersNumber: answers.length,
                                    }}
                                />
                            </Text>
                        </Button>
                    ) : null}

                    <Button
                        variant="text-button"
                        onClick={onShowAnswerTextfield}
                        rightIcon={<GetIcon name="pencil" size="sm" />}
                    >
                        <Text style="bold" size="small">
                            <FormattedMessage defaultMessage="Answer" id="turn-to.answer" />
                        </Text>
                    </Button>
                </Flex>
                <Flex alignItems="center" gap={2}>
                    <GetIcon name="voteup" size="sm" />
                    <Text>{upVotes}</Text>
                </Flex>
            </Flex>
            {isAnswerTextfieldVisible && (
                <AnswerField
                    id={id}
                    onClose={onHideAnswerTextField}
                    type="answer"
                    onCloseField={onHideAnswerTextField}
                />
            )}
            {expandAnswers &&
                answers.map((answer) => <AnswerReplyItem key={answer.id} item={answer} />)}
        </Box>
    );
};
